import styled from "styled-components";
import { typography } from "./Homepage/styles";

/** Fallbacks when rendered outside `.homepage-page` (e.g. Billing). */
const p = (name, fallback) => `var(${name}, ${fallback})`;

export const NftRoot = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  font-family: ${typography.fontFamily};
`;

export const NftPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 20px;
  border: 1px solid hsl(${p("--hp-border", "230 12% 88%")} / 0.6);
  border-radius: 0.75rem;
  background: hsl(${p("--hp-muted", "40 20% 93%")} / 0.3);
`;

export const NftHint = styled.p`
  margin: 0;
  color: hsl(${p("--hp-muted-foreground", "230 10% 40%")});
  font-size: 0.875rem;
  line-height: 1.5;
`;

export const NftPrimaryBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: fit-content;
  min-height: 2.75rem;
  padding: 0 1.5rem;
  border: none;
  border-radius: 0.5rem;
  background-image: ${p(
    "--hp-gradient-gold",
    "linear-gradient(135deg, hsl(32 95% 48%) 0%, hsl(40 85% 45%) 100%)"
  )};
  box-shadow: 0 4px 20px hsl(32 95% 55% / 0.35);
  color: hsl(${p("--hp-primary-foreground", "0 0% 100%")});
  font-family: ${typography.fontFamily};
  font-size: 0.9375rem;
  font-weight: ${typography.weights.bold};
  line-height: 1;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease, opacity 0.2s ease;

  svg {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  }

  &:hover:not(:disabled) {
    transform: scale(1.02);
    box-shadow: 0 6px 28px hsl(32 95% 55% / 0.45);
  }

  &:active:not(:disabled) {
    transform: scale(0.98);
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
    pointer-events: none;
  }

  &:focus-visible {
    outline: none;
    box-shadow:
      0 0 0 2px hsl(${p("--hp-background", "40 30% 99%")}),
      0 0 0 4px hsl(${p("--hp-ring", "32 95% 48%")}),
      0 4px 20px hsl(32 95% 55% / 0.35);
  }
`;

export const NftAccountRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
`;

export const NftChipBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-height: 2.25rem;
  padding: 0 12px;
  border: 1px solid hsl(${p("--hp-border", "230 12% 88%")} / 0.8);
  border-radius: 0.5rem;
  background: hsl(${p("--hp-card", "0 0% 100%")});
  color: hsl(${p("--hp-foreground", "230 25% 10%")});
  font-family: ${typography.fontFamily};
  font-size: 0.8125rem;
  font-weight: ${typography.weights.medium};
  cursor: pointer;
  transition: border-color 0.15s ease, background-color 0.15s ease;

  &:hover {
    border-color: hsl(${p("--hp-primary", "32 95% 48%")} / 0.5);
    background: hsl(${p("--hp-primary", "32 95% 48%")} / 0.06);
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 2px hsl(${p("--hp-ring", "32 95% 48%")} / 0.45);
  }
`;

export const NftWrongNetworkBtn = styled(NftPrimaryBtn)`
  background-image: none;
  background: hsl(0 72% 51%);
  box-shadow: 0 4px 16px hsl(0 72% 51% / 0.28);

  &:hover:not(:disabled) {
    box-shadow: 0 6px 22px hsl(0 72% 51% / 0.35);
  }
`;

export const NftMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 12px 14px;
  border: 1px solid hsl(${p("--hp-border", "230 12% 88%")} / 0.6);
  border-radius: 0.5rem;
  background: hsl(${p("--hp-card", "0 0% 100%")});
`;

export const NftMetaLabel = styled.span`
  color: hsl(${p("--hp-muted-foreground", "230 10% 40%")});
  font-size: 0.75rem;
  font-weight: ${typography.weights.medium};
  letter-spacing: 0.02em;
  text-transform: uppercase;
`;

export const NftMetaValue = styled.span`
  color: hsl(${p("--hp-foreground", "230 25% 10%")});
  font-size: 0.875rem;
  font-weight: ${typography.weights.medium};
  word-break: break-all;
  line-height: 1.4;
`;

export const NftStatusOk = styled.p`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin: 0;
  color: hsl(${p("--hp-foreground", "230 25% 10%")});
  font-size: 0.875rem;
  font-weight: ${typography.weights.medium};
  line-height: 1.45;

  svg {
    width: 16px;
    height: 16px;
    margin-top: 2px;
    flex-shrink: 0;
    color: hsl(${p("--hp-primary", "32 95% 48%")});
  }
`;

export const NftStatusError = styled.p`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin: 0;
  padding: 12px 14px;
  border: 1px solid hsl(0 72% 51% / 0.35);
  border-radius: 0.5rem;
  background: hsl(0 72% 51% / 0.08);
  color: hsl(0 65% 40%);
  font-size: 0.875rem;
  font-weight: ${typography.weights.medium};
  line-height: 1.45;

  svg {
    width: 16px;
    height: 16px;
    margin-top: 2px;
    flex-shrink: 0;
  }
`;

export const NftVerified = styled.p`
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  padding: 12px 14px;
  border: 1px solid hsl(${p("--hp-primary", "32 95% 48%")} / 0.4);
  border-radius: 0.5rem;
  background: hsl(${p("--hp-primary", "32 95% 48%")} / 0.08);
  color: hsl(${p("--hp-foreground", "230 25% 10%")});
  font-size: 0.875rem;
  font-weight: ${typography.weights.semibold};
  line-height: 1.4;

  svg {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
    color: hsl(${p("--hp-primary", "32 95% 48%")});
  }
`;
